import { RawGetSubscribeMappList, RawGetTicketSubscribeList, type SubscribeMappListDto } from '@entities/subscribes'
import { podoFetcher, toLegacyPodoErrorResponse } from '@server/infrastructure/external-apis/podo/client'
import { rpc } from '@server/infrastructure/rpc'
import {
  BaseResponseSchema,
  GetSubscribeTicketsReqSchema,
  LegacyBaseResponseSchema,
  RawSubscribeTicket,
  SubscribeTicket,
} from '@server/schemas'
import { toCamelCase } from '@server/shared/utils/case-adapter'

import { getBearerToken } from '../../authentication'

const toLegacyBaseResponse = <T>({
  resultCd,
  message,
  data,
}: {
  resultCd: number
  message: string | null
  data: T
}): LegacyBaseResponseSchema<T> & { resultMsg?: string } => ({
  resultCd,
  ...(message ? { resultMsg: message } : {}),
  result: data,
})

const toExpectedNotFoundLegacyBaseResponse = <T>(
  error: unknown,
  fallbackResult: T,
): LegacyBaseResponseSchema<T> & { resultMsg?: string } => {
  const response = toLegacyPodoErrorResponse<T>(error)

  if (response?.resultCd === '404') {
    return {
      resultCd: Number(response.resultCd),
      ...(response.resultMsg ? { resultMsg: response.resultMsg } : {}),
      result: response.result ?? fallbackResult,
    }
  }

  throw error
}

export const getSubscribeMappList = async (
  bearerToken?: string,
  options?: { signal?: AbortSignal; timeoutMs?: number },
): Promise<LegacyBaseResponseSchema<RawGetSubscribeMappList[]>> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  try {
    const response = await podoFetcher<RawGetSubscribeMappList[]>(
      {
        url: '/api/v1/subscribe/podo/getSubscribeMappList',
        method: 'GET',
        header: {
          Authorization: `Bearer ${authorizationToken}`,
        },
      },
      options,
    )

    return toLegacyBaseResponse(response)
  } catch (error) {
    return toExpectedNotFoundLegacyBaseResponse<RawGetSubscribeMappList[]>(error, [])
  }
}

export const getSubscribeMappUsing = async (bearerToken?: string): Promise<SubscribeMappListDto[]> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<{
    resultCd: number
    resultCdName: string
    message: string | null
    data: SubscribeMappListDto[]
  }>({
    url: '/api/v1/subscribe/podo/subscribe-mapp?listType=USING',
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SubscribeMappListDto[]>(response.data)
}

export const getSubscribeMappUsed = async (bearerToken?: string): Promise<SubscribeMappListDto[]> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<{
    resultCd: number
    resultCdName: string
    message: string | null
    data: SubscribeMappListDto[]
  }>({
    url: '/api/v1/subscribe/podo/subscribe-mapp?listType=USED',
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return toCamelCase<SubscribeMappListDto[]>(response.data)
}

export const getTicketSubscribeList = async (bearerToken?: string) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  try {
    const response = await podoFetcher<RawGetTicketSubscribeList[]>({
      url: '/api/v1/subscribe/podo/getTicketSubscribeList?listType=USING',
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    })

    return toLegacyBaseResponse(response)
  } catch (error) {
    return toExpectedNotFoundLegacyBaseResponse<RawGetTicketSubscribeList[]>(error, [])
  }
}

export const getTrialSubscribesByStudentId = async ({
  bearerToken,
  studentId,
  timeoutMs,
}: RequestQueryOptionalOptions<{ studentId: number }> & { timeoutMs?: number }) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())

  return await podoFetcher<BaseResponseSchema<RawSubscribeTicket[]>>(
    {
      url: `/api/v1/subscribe/meta/podo/trial?studentId=${studentId}&catalogProtocol=KR_PRODUCT_V1`,
      method: 'GET',
      header: {
        Authorization: `Bearer ${authorizationToken}`,
      },
    },
    { timeoutMs },
  )
}

export const fetchSubscribeTickets = async (
  requestParams: GetSubscribeTicketsReqSchema,
  bearerToken?: string,
): Promise<SubscribeTicket[]> => {
  const response = await rpc.api.v1.subscribes.tickets.$get(
    {
      query: {
        paymentType: requestParams.paymentType,
        lessonCountPerMonth: requestParams.lessonCountPerMonth,
        langType: requestParams.langType,
        lessonTime: requestParams.lessonTime,
        curriculumType: requestParams.curriculumType,
        promotionType: requestParams.promotionType,
      },
    },
    bearerToken ? { headers: { Authorization: `Bearer ${bearerToken}` } } : undefined,
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch subscribe tickets: ${response.status}`)
  }

  return response.json() as Promise<SubscribeTicket[]>
}
